/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useCallback, useEffect, useRef, useState } from "react";
import { useVideoPlayer2 } from "../hooks/use-video-player-2-setup-start";

export function DoubleSpeedTap() {
  const player = useVideoPlayer2();
  const [isActive, setIsActive] = useState(false);
  const [originalSpeed, setOriginalSpeed] = useState(1);
  const [isRightHalf, setIsRightHalf] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartTimeRef = useRef<number>(0);
  const isDoubleSpeedActiveRef = useRef(false);
  const hintTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Only render if the feature is explicitly enabled
  if (player.props.enableDoubleSpeedTap !== true) {
    return null;
  }

  // Show hint on first interaction
  useEffect(() => {
    if (!hasInteracted && player.isPlaying) {
      const timer = setTimeout(() => {
        setShowHint(true);
        hintTimeoutRef.current = setTimeout(() => {
          setShowHint(false);
        }, 3000);
      }, 2000); // Show hint after 2 seconds of playing
      
      return () => clearTimeout(timer);
    }
  }, [hasInteracted, player.isPlaying]);

  // Handle mouse/touch events
  const handlePointerDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!containerRef.current) return;
    
    // Prevent default to avoid conflicts with other handlers
    e.preventDefault();
    e.stopPropagation();
    
    setHasInteracted(true);
    setShowHint(false);
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current);
    }
    
    const rect = containerRef.current.getBoundingClientRect();
    const point = 'touches' in e ? e.touches[0] ?? e.changedTouches[0] : e;
    if (!point) return;
    const x = point.clientX - rect.left;
    const isRightSide = x > rect.width / 2;
    
    setIsRightHalf(isRightSide);
    touchStartTimeRef.current = Date.now();
    
    // Only activate on right half
    if (isRightSide) {
      // Store original speed if not already in double speed mode
      if (!isDoubleSpeedActiveRef.current) {
        setOriginalSpeed(player.speed);
        
        // Add a small delay to prevent accidental activation
        activationTimeoutRef.current = setTimeout(() => {
          player.setSpeed(2);
          setIsActive(true);
          isDoubleSpeedActiveRef.current = true;
        }, 100); // 100ms delay
      }
    }
  }, [player]);

  const handlePointerUp = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Clear any pending activation
    if (activationTimeoutRef.current) {
      clearTimeout(activationTimeoutRef.current);
      activationTimeoutRef.current = null;
    }
    
    if (isDoubleSpeedActiveRef.current) {
      // Restore original speed
      player.setSpeed(originalSpeed);
      setIsActive(false);
      isDoubleSpeedActiveRef.current = false;
    }
  }, [player, originalSpeed]);

  const handlePointerLeave = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Clear any pending activation
    if (activationTimeoutRef.current) {
      clearTimeout(activationTimeoutRef.current);
      activationTimeoutRef.current = null;
    }
    
    if (isDoubleSpeedActiveRef.current) {
      // Restore original speed when leaving the area
      player.setSpeed(originalSpeed);
      setIsActive(false);
      isDoubleSpeedActiveRef.current = false;
    }
  }, [player, originalSpeed]);

  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if video player is focused
      if (!containerRef.current?.contains(document.activeElement)) return;
      
      if (e.key === 'ArrowRight' && e.shiftKey) {
        e.preventDefault();
        if (!isDoubleSpeedActiveRef.current) {
          setOriginalSpeed(player.speed);
          player.setSpeed(2);
          setIsActive(true);
          isDoubleSpeedActiveRef.current = true;
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && e.shiftKey && isDoubleSpeedActiveRef.current) {
        e.preventDefault();
        player.setSpeed(originalSpeed);
        setIsActive(false);
        isDoubleSpeedActiveRef.current = false;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [player, originalSpeed]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (isDoubleSpeedActiveRef.current) {
        player.setSpeed(originalSpeed);
      }
    };
  }, [player, originalSpeed]);

  return (
    <div
      ref={containerRef}
      className="video-player-2-double-speed-tap"
      onMouseDown={handlePointerDown}
      onMouseUp={handlePointerUp}
      onMouseLeave={handlePointerLeave}
      onTouchStart={handlePointerDown}
      onTouchEnd={handlePointerUp}
      onTouchCancel={handlePointerLeave}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        pointerEvents: 'auto',
        cursor: 'pointer'
      }}
    >
      {/* Hint overlay for first-time users */}
      {showHint && !hasInteracted && (
        <div className="video-player-2-double-speed-tap__hint">
          <div className="video-player-2-double-speed-tap__hint-text">
            Tap right side for 2x speed
          </div>
        </div>
      )}
      
      {/* Visual feedback overlay */}
      {isActive && isRightHalf && (
        <div className="video-player-2-double-speed-tap__overlay">
          <div className="video-player-2-double-speed-tap__indicator">
            <div className="video-player-2-double-speed-tap__speed">2x</div>
            <div className="video-player-2-double-speed-tap__text">Speed</div>
          </div>
        </div>
      )}
    </div>
  );
}
